/**
 * Dynamic Testimonials - Fetch reviews from Kiyoh API
 * Fetches testimonials from Kiyoh feedback API and renders them dynamically
 */

(function() {
  'use strict';

  const API_URL = 'https://feedback.monkeydo.it/api/v1/review/271062';
  const XML_API_URL = 'https://www.kiyoh.com/v1/review/feed.xml?hash=gnbsoiynpcuvmxg';
  const KIYOH_REVIEWS_URL = 'https://www.kiyoh.com/reviews/1064942/123impregneer';
  const CACHE_KEY = 'kiyoh_reviews_data';
  const CACHE_KEY_AGGREGATE = 'kiyoh_aggregate_data';
  const CACHE_DURATION = 3600000; // 1 hour in milliseconds

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const container = document.querySelector('[data-testimonials-container]');
    if (!container) return;

    const loadingSpinner = document.querySelector('[data-testimonials-loading]');
    const errorContainer = document.querySelector('[data-testimonials-error]');

    // Get configuration from data attributes
    const config = {
      sectionId: container.dataset.sectionId,
      columns: parseInt(container.dataset.columns) || 4,
      layout: container.dataset.layout || 'grid',
      cardClass: container.dataset.cardClass || '',
      stackMobile: container.dataset.stackMobile === 'true'
    };

    // Check if we have cached data
    const cachedReviews = getCachedReviews();
    const cachedAggregate = getCachedAggregate();

    if (cachedReviews && cachedAggregate) {
      renderReviews(cachedReviews, container, config);
      renderKiyohRating(cachedAggregate);
      renderSubheading(cachedAggregate);
      hideLoading(loadingSpinner, errorContainer);
    } else {
      // Fetch both reviews and aggregate data
      Promise.all([
        fetchReviews(),
        fetchAggregateData()
      ])
        .then(([reviewsData, aggregateData]) => {
          cacheReviews(reviewsData);
          cacheAggregate(aggregateData);
          renderReviews(reviewsData, container, config);
          renderKiyohRating(aggregateData);
          renderSubheading(aggregateData);
          hideLoading(loadingSpinner, errorContainer);
        })
        .catch(error => {
          console.error('Error fetching testimonials:', error);
          showError(loadingSpinner, errorContainer);
        });
    }
  }

  function getCachedReviews() {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is still valid
      if (data.timestamp && (now - data.timestamp < CACHE_DURATION)) {
        return data.reviews;
      }

      // Cache expired
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    } catch (e) {
      console.error('Error reading cached reviews:', e);
      return null;
    }
  }

  function cacheReviews(data) {
    try {
      const cacheData = {
        reviews: data,
        timestamp: Date.now()
      };
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Error caching reviews:', e);
    }
  }

  function getCachedAggregate() {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY_AGGREGATE);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const now = Date.now();

      if (data.timestamp && (now - data.timestamp < CACHE_DURATION)) {
        return data.aggregate;
      }

      sessionStorage.removeItem(CACHE_KEY_AGGREGATE);
      return null;
    } catch (e) {
      console.error('Error reading cached aggregate data:', e);
      return null;
    }
  }

  function cacheAggregate(data) {
    try {
      const cacheData = {
        aggregate: data,
        timestamp: Date.now()
      };
      sessionStorage.setItem(CACHE_KEY_AGGREGATE, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Error caching aggregate data:', e);
    }
  }

  function fetchAggregateData() {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            const xmlResponse = xhr.responseXML;
            const numberReviewsElement = xmlResponse.getElementsByTagName("numberReviews")[0];
            const averageRatingElement = xmlResponse.getElementsByTagName("averageRating")[0];

            if (!numberReviewsElement || !averageRatingElement) {
              reject(new Error('XML response missing required elements'));
              return;
            }

            const totalReviews = parseInt(numberReviewsElement.textContent);
            const averageRating = parseFloat(averageRatingElement.textContent);

            resolve({
              totalReviews: totalReviews,
              averageRating: averageRating
            });
          } else {
            reject(new Error(`HTTP error! status: ${xhr.status}`));
          }
        }
      };

      xhr.open("GET", XML_API_URL, true);
      xhr.send();
    });
  }

  function fetchReviews() {
    return fetch(API_URL, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  }

  function renderReviews(data, container, config) {
    const reviews = data.reviews || [];

    if (!reviews.length) {
      showError(document.querySelector('[data-testimonials-loading]'),
                document.querySelector('[data-testimonials-error]'));
      return;
    }

    // Build HTML structure
    let html = '';

    // Add scroller wrapper if needed (mobile scrolling)
    const needsScroller = !config.stackMobile && reviews.length > 1;
    if (needsScroller) {
      html += '<div class="scroller"><div class="scroller-inner">';
    }

    // Determine grid classes
    let gridClasses = 'grid grid-1-col';
    if (config.columns >= 4) {
      gridClasses += ` grid-3-col-tablet grid-${config.columns}-col-desktop`;
    } else if (config.columns === 2) {
      gridClasses += ' grid-2-col-tablet';
    } else {
      gridClasses += ' grid-3-col-tablet';
    }

    if (config.layout === 'slider') {
      gridClasses += ' grid-carousel';
    }

    html += `<div class="${gridClasses}">`;

    // Generate testimonial cards
    reviews.forEach(review => {
      const rating = (review.rating / 2).toFixed(1);
      const title = review.author || 'Anonymous';
      const date = review.date || '';

      // Get review content from the content array
      let description = '';
      if (review.content && Array.isArray(review.content)) {
        // Combine title and content from the review
        const reviewTitle = review.content[1]?.answer || '';
        const reviewContent = review.content[2]?.answer || '';

        if (reviewTitle && reviewContent) {
          description = `<p><strong>${escapeHtml(reviewTitle)}</strong></p><p>${escapeHtml(reviewContent)}</p>`;
        } else if (reviewContent) {
          description = `<p>${escapeHtml(reviewContent)}</p>`;
        } else if (reviewTitle) {
          description = `<p>${escapeHtml(reviewTitle)}</p>`;
        }
      }

      html += `
        <div class="grid-item">
          ${generateTestimonialCard(title, description, parseFloat(rating), config.cardClass, date)}
        </div>
      `;
    });

    html += '</div>'; // Close grid

    if (needsScroller) {
      html += '</div></div>'; // Close scroller
    }

    container.innerHTML = html;

    // Initialize carousel for slider layout
    if (config.layout === 'slider') {
      initializeCarousel(container, config);
    }

    // Fade in the container
    setTimeout(() => {
      container.classList.add('loaded');
    }, 100);
  }

  function initializeCarousel(container, config) {
    // Get the grid element
    const grid = container.querySelector('.grid-carousel');
    if (!grid) return;

    // Create carousel-slider wrapper
    const carouselWrapper = document.createElement('carousel-slider');
    carouselWrapper.id = `Carousel-${config.sectionId}`;

    // Store reference to parent and next sibling
    const parent = container.parentNode;
    const nextSibling = container.nextSibling;

    // Add container to wrapper FIRST (before inserting into DOM)
    carouselWrapper.appendChild(container);

    // Insert the wrapper at the original position (before the next sibling)
    // This keeps the CTA below the reviews
    if (nextSibling) {
      parent.insertBefore(carouselWrapper, nextSibling);
    } else {
      parent.appendChild(carouselWrapper);
    }
  }

  function generateTestimonialCard(title, description, rating, cardClass, date) {
    const ratingInteger = Math.floor(rating);
    const decimal = rating % 1;
    let ratingDecimal = 0;

    if (decimal >= 0.3 && decimal <= 0.7) {
      ratingDecimal = 0.5;
    } else if (decimal > 0.7) {
      ratingDecimal = 0;
    }

    const finalRating = decimal > 0.7 ? ratingInteger + 1 : ratingInteger;
    const cardClasses = `card card-testimonial ${cardClass}`.trim();

    // Generate star rating HTML
    let starsHtml = '<div class="rating-stars" role="img" aria-label="Rating: ' + rating + ' out of 5">';

    // Full stars
    for (let i = 0; i < finalRating; i++) {
      starsHtml += `
        <svg class="icon icon-rating-star-full" width="1.5rem" height="1.5rem" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 1.5L12.5 7.5L19 8.5L14.5 13L15.5 19.5L10 16.5L4.5 19.5L5.5 13L1 8.5L7.5 7.5L10 1.5Z" fill="var(--color-product-review-star)" stroke="var(--color-product-review-star)" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    // Half star
    if (ratingDecimal === 0.5) {
      starsHtml += `
        <svg class="icon icon-rating-star-half" width="1.5rem" height="1.5rem" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 1.5L12.5 7.5L19 8.5L14.5 13L15.5 19.5L10 16.5L4.5 19.5L5.5 13L1 8.5L7.5 7.5L10 1.5Z" fill="url(#rating-star-gradient-half)" stroke="var(--color-product-review-star)" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    // Empty stars
    const emptyStars = 5 - finalRating - (ratingDecimal === 0.5 ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      starsHtml += `
        <svg class="icon icon-rating-star-empty" width="1.5rem" height="1.5rem" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 1.5L12.5 7.5L19 8.5L14.5 13L15.5 19.5L10 16.5L4.5 19.5L5.5 13L1 8.5L7.5 7.5L10 1.5Z" fill="none" stroke="color-mix(in srgb, rgba(var(--color-foreground)), transparent 50%)" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" opacity="0.5"/>
        </svg>
      `;
    }

    starsHtml += '</div>';

    // Format date - extract only the date part (remove time)
    let formattedDate = '';
    if (date) {
      // Split by space to remove time part (e.g., "2025-11-11 08:00:42" -> "2025-11-11")
      const dateOnly = date.split(' ')[0];
      // Optionally reformat to DD-MM-YYYY
      const parts = dateOnly.split('-');
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      } else {
        formattedDate = dateOnly;
      }
    }

    return `
      <blockquote class="${cardClasses}" style="position: relative; padding-bottom: ${formattedDate ? '3rem' : '2rem'};">
        <div class="card-testimonial-header">
          <div class="card-testimonial-author">
            <cite class="card-testimonial-citation">${escapeHtml(title)}</cite>
          </div>
          <div class="card-testimonial-rating">
            ${starsHtml}
          </div>
        </div>
        ${description ? `<div class="card-testimonial-content rte">${description}</div>` : ''}
        ${formattedDate ? `<div class="card-testimonial-date" style="position: absolute; bottom: 1.5rem; right: 2rem; font-size: 1.2rem; opacity: 0.6;">${escapeHtml(formattedDate)}</div>` : ''}
      </blockquote>
    `;
  }

  function renderKiyohRating(aggregateData) {
    const kiyohContainer = document.querySelector('[data-kiyoh-rating]');
    if (!kiyohContainer) return;

    const { averageRating, totalReviews } = aggregateData;
    if (!averageRating) return;

    // Convert rating from 0-10 to 0-5 scale for stars
    const ratingOutOf5 = averageRating / 2;
    const fullStars = Math.floor(ratingOutOf5);
    const hasHalfStar = ratingOutOf5 % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    // Build stars HTML using boxicons (like footer)
    let starsHtml = '';
    for (let i = 0; i < fullStars; i++) {
      starsHtml += "<i class='bx bxs-star'></i>";
    }
    if (hasHalfStar) {
      starsHtml += "<i class='bx bxs-star-half'></i>";
    }
    for (let i = 0; i < emptyStars; i++) {
      starsHtml += "<i class='bx bx-star'></i>";
    }

    // Build Kiyoh widget HTML (similar to footer widget)
    kiyohContainer.innerHTML = `
      <a href="${KIYOH_REVIEWS_URL}" target="_blank" rel="noopener noreferrer">
        <div class="testimonials-kiyoh-inner">
          <div class="testimonials-kiyoh-badge">
            <span class="testimonials-kiyoh-score">${averageRating.toFixed(1)}</span>
          </div>
          <div class="testimonials-kiyoh-details">
            <div class="testimonials-kiyoh-stars">
              ${starsHtml}
            </div>
          </div>
        </div>
      </a>
    `;

    // Show the container
    kiyohContainer.style.display = 'flex';
  }

  function renderSubheading(aggregateData) {
    const subheadingContainer = document.querySelector('[data-testimonials-subheading]');
    if (!subheadingContainer) return;

    const { totalReviews } = aggregateData;
    if (!totalReviews) return;

    // Set subheading text in Dutch
    subheadingContainer.textContent = `Gebaseerd op ${totalReviews.toLocaleString('nl-NL')} beoordelingen`;
    subheadingContainer.style.display = 'block';
  }

  function hideLoading(loadingSpinner, errorContainer) {
    if (loadingSpinner) {
      loadingSpinner.classList.add('hidden');
    }
  }

  function showError(loadingSpinner, errorContainer) {
    if (loadingSpinner) {
      loadingSpinner.classList.add('hidden');
    }
    if (errorContainer) {
      errorContainer.classList.add('visible');
    }
  }

  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
})();
